import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { getImageVariantUrl } from 'app/lib/image-variants';
import type { Listing } from 'app/types/listing';
import { useTranslation } from 'react-i18next';
import type { ColorValue, GestureResponderEvent } from 'react-native';
import { View, Pressable } from 'react-native';
import { useMappingHelper } from '@shopify/flash-list';
import { LoanFeeTag } from './listing-card-loan-fee-tag';
import { localeAwareFormatDurationShort } from 'app/lib/date';
import { isMobileNative } from 'app/utils/web-helpers';
import { SuperSharerMedal } from 'app/design/icons/super-sharer-medal';
import { AmbassadorMedal } from 'app/design/icons/ambassador-medal';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { createTheme as createDSTheme } from 'app/shopify';
import { useNavigation } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
const theme = createDSTheme();

interface ListingPostedByMembersProps {
  avatarSize: number;
  grouping?: Listing['grouping'];
  ignoreIsAuthor?: boolean;
  isAuthor: boolean;
  loanFee?: Listing['loanFee'];
  textColor?: ColorValue | string;
  user: Listing['user'];
}

interface MemberBadgeProps {
  ambassadorEnabled: boolean;
  avatarSize: number;
  badgeSize: number;
  isAmbassador?: boolean | null;
  isSuper?: boolean | null;
}

function MemberBadge({ ambassadorEnabled, avatarSize, badgeSize, isAmbassador, isSuper }: MemberBadgeProps) {
  if (ambassadorEnabled && isAmbassador) {
    return (
      <AmbassadorMedal
        width={badgeSize}
        height={badgeSize * (60 / 57)}
        style={{
          position: 'absolute',
          bottom: avatarSize * -0.25,
          right: avatarSize * -0.1,
          opacity: 0.95,
          zIndex: 2,
        }}
      />
    );
  }

  if (!isSuper) return null;

  return (
    <SuperSharerMedal
      width={badgeSize}
      height={badgeSize * (69 / 57)}
      style={{
        position: 'absolute',
        bottom: avatarSize * -0.33,
        right: avatarSize * -0.1,
        opacity: 0.95,
        zIndex: 2,
      }}
    />
  );
}

function GroupedMembers({
  ambassadorEnabled,
  avatarSize,
  badgeSize,
  fixedHeight,
  grouping,
  textColor,
}: Pick<ListingPostedByMembersProps, 'avatarSize' | 'grouping' | 'textColor'> & {
  ambassadorEnabled: boolean;
  badgeSize: number;
  fixedHeight: number;
}) {
  const { t } = useTranslation('all-listings');
  const { getMappingKey } = useMappingHelper();
  const maxNumberOfAvatars = 3;
  const avatarOverlap = theme.spacing['spacing.general.x2'];
  const totalMembers = grouping?.totalOffers ?? 0;

  return (
    <Row alignItems="center" gap="spacing.general.x2" maxWidth={220} height={fixedHeight}>
      <Row alignItems="center">
        {grouping?.listings.slice(0, maxNumberOfAvatars).map((gListing: Listing, index: number) => (
          <View
            key={getMappingKey(gListing.id, index)}
            style={[
              { marginLeft: index > 0 ? -avatarOverlap : 0 },
              {
                ...(isMobileNative
                  ? {
                      shadowColor: theme.colors['text.primary'],
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: 1,
                    }
                  : {}),
                zIndex: totalMembers - index,
              },
            ]}
          >
            <PCImage
              aspectRatio={1}
              cachePolicy="memory-disk"
              height={avatarSize}
              recyclingKey={`${gListing.user.profile.picture!}-card`}
              source={{ uri: getImageVariantUrl(gListing.user.profile.picture, 'mini') }}
              style={{
                borderColor: 'white',
                borderWidth: 1,
                borderRadius: theme.borderRadii['radius.xl'],
              }}
            />
            {index <= Math.min(maxNumberOfAvatars, totalMembers) - 1 ? (
              <MemberBadge
                ambassadorEnabled={ambassadorEnabled}
                avatarSize={avatarSize}
                badgeSize={badgeSize}
                isAmbassador={gListing.user.isAmbassador}
                isSuper={gListing.user.isSuper}
              />
            ) : null}
          </View>
        ))}
      </Row>
      <Text ellipsizeMode="tail" flexShrink={1} numberOfLines={1} style={{ color: textColor }} variant="bodySmall">
        {totalMembers > 100 ? t('groupingMembersMany', { count: 99 }) : t('groupingMembers', { count: totalMembers })}
      </Text>
    </Row>
  );
}

function SingleMember({
  ambassadorEnabled,
  avatarSize,
  badgeSize,
  fixedHeight,
  isAuthor,
  onPress,
  textColor,
  user,
}: Pick<ListingPostedByMembersProps, 'avatarSize' | 'isAuthor' | 'textColor' | 'user'> & {
  ambassadorEnabled: boolean;
  badgeSize: number;
  fixedHeight: number;
  onPress: (event: GestureResponderEvent) => void;
}) {
  const { t } = useTranslation('all-listings');
  const fontSize = 12;
  const fullName = `${user.profile.firstName} ${user.profile.lastName}`;
  const displayName = fullName.length > 15 ? `${fullName.substring(0, 15)}...` : fullName;

  return (
    <Pressable
      accessibilityLabel={t('profile.viewMember', { ns: 'accessibility', name: fullName })}
      accessibilityRole="button"
      onPress={onPress}
      style={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: theme.spacing['spacing.general.x2'],
        maxWidth: 160,
        height: fixedHeight,
      }}
    >
      {user.profile.picture ? (
        <View>
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={avatarSize}
            recyclingKey={`${user.profile.picture}-card`}
            source={{ uri: getImageVariantUrl(user.profile.picture, 'mini') }}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
          />
          <MemberBadge
            ambassadorEnabled={ambassadorEnabled}
            avatarSize={avatarSize}
            badgeSize={badgeSize}
            isAmbassador={user.isAmbassador}
            isSuper={user.isSuper}
          />
        </View>
      ) : null}

      <Column>
        <Text
          ellipsizeMode="tail"
          numberOfLines={1}
          fontSize={14}
          fontWeight={500}
          style={{ color: textColor, top: !user.trustPoints && !user.lastSeenAt ? fontSize / 2 : 0, maxWidth: 180 }}
          variant="bodyBase"
        >
          {displayName}
        </Text>
        <Row alignItems="center" gap="spacing.general.x1" minHeight={fontSize}>
          {!isAuthor && user.trustPoints ? (
            <Text
              fontSize={fontSize}
              color="text.placeholder"
              numberOfLines={1}
              style={{ width: '100%' }}
              variant="bodyBase"
            >
              {`👍 (${user.trustPoints.toFixed(0)})`}
              {user.lastSeenAt ? ' | ' : ''}
              {user.lastSeenAt
                ? t('lastActiveAgoShort', {
                    ago: localeAwareFormatDurationShort(new Date(user.lastSeenAt)),
                    ns: 'common',
                  })
                : ''}
            </Text>
          ) : !isAuthor && user.lastSeenAt ? (
            <Text
              fontSize={fontSize}
              color="text.placeholder"
              numberOfLines={1}
              style={{ width: '100%' }}
              variant="bodyBase"
            >
              {t('lastActiveAgoShort', {
                ago: localeAwareFormatDurationShort(new Date(user.lastSeenAt)),
                ns: 'common',
              })}
            </Text>
          ) : null}
        </Row>
      </Column>
    </Pressable>
  );
}

function ListingPostedByMembers(props: ListingPostedByMembersProps) {
  const navigation = useNavigation();
  const authorizeProfile = useAuthorize('member-profile');
  const ambassadorEnabled = useAmbassador().isEnabled;

  const { avatarSize, ignoreIsAuthor, isAuthor, loanFee, textColor, grouping, user } = props;
  const badgeSize = Math.round(avatarSize * 0.65);

  const fixedHeight = isMobileNative ? 30 : Math.max(avatarSize, 30);
  const isGroupedListing = Boolean(!isAuthor && grouping);

  if (isGroupedListing) {
    return (
      <GroupedMembers
        ambassadorEnabled={ambassadorEnabled}
        avatarSize={avatarSize}
        badgeSize={badgeSize}
        fixedHeight={fixedHeight}
        grouping={grouping}
        textColor={textColor}
      />
    );
  }

  if (isAuthor && !ignoreIsAuthor) return <LoanFeeTag loanFee={loanFee} />;

  return (
    <SingleMember
      ambassadorEnabled={ambassadorEnabled}
      avatarSize={avatarSize}
      badgeSize={badgeSize}
      fixedHeight={fixedHeight}
      isAuthor={isAuthor}
      onPress={(event) => {
        event.stopPropagation();
        authorizeProfile(() => navigateToUserProfile(navigation, user.id));
      }}
      textColor={textColor}
      user={user}
    />
  );
}
export { ListingPostedByMembers };
